import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import {
  ArrowLeft, Briefcase, CheckCircle2, ClipboardList, ListChecks, Loader2,
  LogIn, Paperclip, Send, ShieldAlert, X,
} from "lucide-react";
import { useAuth } from "../lib/auth";
import { API } from "../lib/api";
import { Button } from "../components/ui/button";
import { useToast } from "../hooks/use-toast";

const STATUS_LABEL = {
  new: "Eingereicht", in_review: "In Prüfung", accepted: "Angenommen", rejected: "Abgelehnt",
};

export default function JobDetail() {
  const { id } = useParams();
  const { user, userToken } = useAuth();
  const { toast } = useToast();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [mine, setMine] = useState([]);
  const [message, setMessage] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    axios.get(`${API}/jobs/${id}`).then((r) => setJob(r.data)).catch(() => setNotFound(true));
  }, [id]);

  useEffect(() => {
    if (!userToken) { setMine([]); return; }
    axios.get(`${API}/jobs/applications/mine`, { headers: { Authorization: `Bearer ${userToken}` } })
      .then((r) => setMine(r.data || [])).catch(() => {});
  }, [userToken, submitted]);

  const myApplication = useMemo(() => mine.find((a) => a.job_id === id), [mine, id]);

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const fd = new FormData();
      fd.append("message", message.trim());
      if (file) fd.append("attachment", file);
      await axios.post(`${API}/jobs/${id}/apply`, fd, {
        headers: { Authorization: `Bearer ${userToken}` },
      });
      setSubmitted(true);
      toast({ title: "Bewerbung abgeschickt", description: "Du hast eine Bestätigung in deinem Habthek-Postfach." });
    } catch (err) {
      const d = err?.response?.data?.detail;
      toast({ title: "Fehler", description: typeof d === "string" ? d : "Bewerbung fehlgeschlagen", variant: "destructive" });
    } finally {
      setSending(false);
    }
  };

  if (notFound) {
    return (
      <section className="mx-auto max-w-3xl px-4 py-24 text-center" data-testid="job-not-found">
        <Briefcase className="mx-auto h-8 w-8 text-zinc-600" />
        <h1 className="mt-3 text-2xl font-bold text-white">Job nicht gefunden</h1>
        <p className="mt-2 text-sm text-zinc-500">Diese Position existiert nicht oder ist nicht mehr offen.</p>
        <Link to="/jobs" className="mt-5 inline-flex items-center gap-1 text-sm font-semibold text-[#b39bff] hover:text-white">
          <ArrowLeft className="h-4 w-4" /> Zurück zu allen Jobs
        </Link>
      </section>
    );
  }
  if (!job) {
    return <div className="flex justify-center py-32"><Loader2 className="h-6 w-6 animate-spin text-zinc-500" /></div>;
  }

  const clean = DOMPurify.sanitize(job.description_html || "", {
    ADD_TAGS: ["table", "thead", "tbody", "tr", "td", "th", "hr"],
    ADD_ATTR: ["class", "style", "colspan", "rowspan", "width", "height", "align"],
  });

  return (
    <section className="mx-auto max-w-4xl px-4 py-12" data-testid="job-detail-page">
      <Link to="/jobs" data-testid="job-back-link" className="inline-flex items-center gap-1 text-sm text-zinc-500 hover:text-white">
        <ArrowLeft className="h-4 w-4" /> Alle Jobs
      </Link>

      {/* Header */}
      <div className="relative mt-4 overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b] px-6 py-10 sm:px-10">
        <div className="pointer-events-none absolute -right-20 -top-20 h-60 w-60 rounded-full bg-[#7c4dff]/20 blur-3xl" />
        <div className="relative">
          <span className="rounded-full bg-[#7c4dff]/15 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.18em] text-[#b39bff]">
            {job.category}
          </span>
          <h1 className="mt-3 text-3xl font-bold text-white sm:text-4xl" data-testid="job-title">{job.title}</h1>
          {job.short_description && (
            <p className="mt-3 max-w-2xl text-base italic leading-relaxed text-zinc-400">{job.short_description}</p>
          )}
          <a href="#bewerben" data-testid="job-apply-cta">
            <Button className="btn-primary mt-6 rounded-lg px-5 py-2 text-sm font-semibold">
              Jetzt bewerben
            </Button>
          </a>
        </div>
      </div>

      {/* Beschreibung */}
      {clean && (
        <div className="mt-6 overflow-hidden rounded-2xl border border-white/10 bg-[#0f141b] px-6 py-8 sm:px-10">
          <div className="news-content min-w-0" lang="de" dangerouslySetInnerHTML={{ __html: clean }} />
        </div>
      )}

      {/* Aufgaben + Anforderungen */}
      {(job.responsibilities?.length > 0 || job.requirements?.length > 0) && (
        <div className="mt-6 grid grid-cols-1 gap-6 md:grid-cols-2">
          {job.responsibilities?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-6" data-testid="job-responsibilities">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9d7bff]">
                <ClipboardList className="h-4 w-4" /> Aufgabenbereich
              </div>
              <ul className="mt-4 space-y-2.5">
                {job.responsibilities.map((r, i) => (
                  <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-zinc-300">
                    <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-sm bg-[#7c4dff]" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {job.requirements?.length > 0 && (
            <div className="rounded-2xl border border-white/10 bg-[#0f141b] p-6" data-testid="job-requirements">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#ff9800]">
                <ListChecks className="h-4 w-4" /> Anforderungen
              </div>
              <ul className="mt-4 space-y-2.5">
                {job.requirements.map((r, i) => (
                  <li key={i} className="flex gap-2.5 text-sm leading-relaxed text-zinc-300">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[#ff9800]/80" />
                    {r}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Bewerbungsformular */}
      <div id="bewerben" className="mt-6 rounded-2xl border border-[#7c4dff]/25 bg-[#12101f] p-6 sm:p-8" data-testid="job-apply-section">
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-[#9d7bff]">
          <Send className="h-4 w-4" /> Bewerbungsformular
        </div>

        {!userToken ? (
          <div className="mt-4" data-testid="job-apply-login-gate">
            <div className="text-lg font-bold text-white">Hoppla! Bitte melde dich an, um dich zu bewerben.</div>
            <p className="mt-1 text-sm text-zinc-400">Bewerbungen laufen über deinen Habthek-Account – so bekommst du die Bestätigung direkt in dein Postfach.</p>
            <div className="mt-4 flex gap-2">
              <Link to="/login"><Button className="btn-primary rounded-lg px-4 py-2 text-sm font-semibold"><LogIn className="mr-1.5 h-4 w-4" /> Einloggen</Button></Link>
              <Link to="/registrieren"><Button className="btn-ghost rounded-lg px-4 py-2 text-sm">Registrieren</Button></Link>
            </div>
          </div>
        ) : user && !user.verified ? (
          <div className="mt-4" data-testid="job-apply-verify-gate">
            <div className="flex items-center gap-2 text-lg font-bold text-white"><ShieldAlert className="h-5 w-5 text-amber-400" /> Verifizierung nötig</div>
            <p className="mt-1 text-sm text-zinc-400">Nur verifizierte Habbos können sich bewerben. Verifiziere kurz dein Profil über dein Habbo-Motto.</p>
            <Link to="/profil"><Button className="btn-primary mt-4 rounded-lg px-4 py-2 text-sm font-semibold">Jetzt verifizieren</Button></Link>
          </div>
        ) : myApplication || submitted ? (
          <div className="mt-4" data-testid="job-apply-done">
            <div className="flex items-center gap-2 text-lg font-bold text-emerald-300">
              <CheckCircle2 className="h-5 w-5" /> Bewerbung eingereicht
            </div>
            <p className="mt-1 text-sm text-zinc-400">
              Status: <span className="font-semibold text-zinc-200">{STATUS_LABEL[myApplication?.status] || "Eingereicht"}</span> – Updates bekommst du in dein Habthek-Postfach (Glocke oben rechts).
            </p>
            <Link to="/meine-bewerbungen" data-testid="job-my-applications-link" className="mt-3 inline-block text-sm font-semibold text-[#b39bff] hover:text-white">
              Zum Austausch mit dem Team → Meine Bewerbungen
            </Link>
          </div>
        ) : (
          <form onSubmit={submit} className="mt-4 space-y-4" data-testid="job-apply-form">
            <div className="text-sm text-zinc-400">
              Du bewirbst dich als <span className="font-semibold text-white">{user?.habbo_name}</span>. Die Bestätigung landet in deinem Habthek-Postfach.
            </div>
            <textarea
              data-testid="job-apply-message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={6}
              placeholder="Erzähl uns, warum du perfekt für diese Position bist…"
              className="w-full rounded-xl border border-white/10 bg-[#10141b] p-4 text-sm text-zinc-200 outline-none placeholder:text-zinc-600 focus:border-[#9d7bff]/50"
            />
            <div className="flex flex-wrap items-center gap-3">
              <input
                ref={fileRef}
                type="file"
                accept=".pdf,.png,.jpg,.jpeg,.gif,.webp"
                className="hidden"
                data-testid="job-apply-file-input"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
              />
              <Button type="button" onClick={() => fileRef.current?.click()} data-testid="job-apply-file-btn" className="btn-ghost rounded-lg px-3 py-2 text-xs">
                <Paperclip className="mr-1.5 h-3.5 w-3.5" /> {file ? "Anhang ändern" : "Anhang (PDF/Bild, optional)"}
              </Button>
              {file && (
                <span className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-3 py-1 text-xs text-zinc-300" data-testid="job-apply-file-name">
                  {file.name}
                  <button type="button" onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-zinc-500 hover:text-white"><X className="h-3 w-3" /></button>
                </span>
              )}
            </div>
            <Button type="submit" disabled={sending || message.trim().length < 10} data-testid="job-apply-submit" className="btn-primary rounded-lg px-6 py-2 text-sm font-semibold">
              {sending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Bewerbung abschicken
            </Button>
          </form>
        )}
      </div>
    </section>
  );
}
